// Industries section — full page content per sector

const build = (kicker, title, tagline, intro, pillars, stats = []) => ({
  kicker, title, tagline, intro, pillars, stats, faqs: [],
  cta: { title: "Initialize project.", sub: "Scope your engagement.", href: "/terminal" },
});

const bullets = (arr) => arr.map((b) => (typeof b === "string" ? { title: b, desc: "" } : b));

export const industriesContent = {
  "aerospace-defense": build(
    "// IND.AEROSPACE",
    "Aerospace & Defense — mission-critical, mission-clear.",
    "Engineering rigor meets defense-grade compliance.",
    "For aerospace primes, defense contractors, and space companies, we build platforms that meet ITAR, CMMC, and DoD-grade security while accelerating delivery.",
    [
      { id: "01", label: "COMPLIANCE.FIRST", body: "CMMC, ITAR, and FedRAMP-aligned architectures.", bullets: bullets(["CMMC readiness", "ITAR data separation"]) },
      { id: "02", label: "ENGINEERING.PLATFORMS", body: "MBSE, digital thread, and PLM integrations.", bullets: bullets(["Digital thread", "PLM connectors"]) },
      { id: "03", label: "OPERATIONAL.AI", body: "AI for maintenance, ISR analysis, and mission planning.", bullets: bullets(["Predictive maintenance"]) },
    ]
  ),

  "asset-wealth-management": build(
    "// IND.AWM",
    "Asset & Wealth Management — modernize the operating platform.",
    "Portfolio ops, advisor experience, and data.",
    "We modernize asset managers and wealth firms — from portfolio operations and advisor desktops to client-facing digital experiences.",
    [
      { id: "01", label: "PORTFOLIO.OPS", body: "Middle- and back-office modernization with real-time position telemetry.", bullets: bullets(["Position mgmt", "Reconciliation"]) },
      { id: "02", label: "ADVISOR.EXPERIENCE", body: "Advisor desktop platforms with integrated CRM, research, and portfolio views.", bullets: bullets(["Integrated desktops"]) },
      { id: "03", label: "CLIENT.DIGITAL", body: "Investor portals, statements, and self-serve tools.", bullets: bullets(["White-label portals"]) },
    ]
  ),

  "banking-capital-markets": build(
    "// IND.BCM",
    "Banking & Capital Markets — engineered for regulation and velocity.",
    "Core modernization, risk, and digital banking.",
    "We help banks and capital markets firms modernize cores, digitize channels, and quantify risk — with SOX and Basel-grade rigor.",
    [
      { id: "01", label: "CORE.MODERNIZATION", body: "Progressive migration off legacy cores into composable platforms.", bullets: bullets(["Strangler-fig", "Composable APIs"]) },
      { id: "02", label: "RISK.PLATFORMS", body: "Market, credit, and operational risk platforms.", bullets: bullets(["FRTB-ready", "Credit models"]) },
      { id: "03", label: "DIGITAL.CHANNELS", body: "Retail and commercial banking channels engineered for scale.", bullets: bullets(["Mobile-first", "Open banking"]) },
    ]
  ),

  "consumer-goods": build(
    "// IND.CG",
    "Consumer Goods — DTC platforms and supply chain intelligence.",
    "Where brand meets operations.",
    "We build platforms that let consumer goods brands move from wholesale to direct-to-consumer while operating supply chains with intelligence.",
    [
      { id: "01", label: "DTC.COMMERCE", body: "Composable commerce architectures with headless storefronts.", bullets: bullets(["Headless commerce"]) },
      { id: "02", label: "SUPPLY.CHAIN", body: "Demand forecasting and supply chain visibility.", bullets: bullets(["Demand ML", "Visibility platforms"]) },
      { id: "03", label: "CONSUMER.DATA", body: "First-party data platforms and identity resolution.", bullets: bullets(["CDP builds"]) },
    ]
  ),

  "construction-infrastructure": build(
    "// IND.CONSTRUCT",
    "Construction & Infrastructure — digitize the field.",
    "Field ops, BIM, and infrastructure delivery.",
    "For construction firms and infrastructure operators, we digitize field operations, integrate BIM data, and accelerate capital project delivery.",
    [
      { id: "01", label: "FIELD.OPS", body: "Mobile-first field applications with offline capability.", bullets: bullets(["Offline sync"]) },
      { id: "02", label: "BIM.INTEGRATION", body: "BIM data flowing across design, construction, and operations.", bullets: bullets(["Digital twin"]) },
      { id: "03", label: "CAPITAL.PROJECTS", body: "Cost, schedule, and safety telemetry in real time.", bullets: bullets(["Portfolio dashboards"]) },
    ]
  ),

  "digital-assets-blockchain": build(
    "// IND.DIGITAL_ASSETS",
    "Digital Assets & Blockchain — infrastructure and compliance.",
    "Custody, tokenization, and regulated crypto ops.",
    "We build the platforms institutional players need to operate in digital assets — custody integrations, tokenization stacks, and compliance-grade infrastructure.",
    [
      { id: "01", label: "CUSTODY.INTEGRATION", body: "Multi-custodian integration platforms with segregation of duties.", bullets: bullets(["Multi-sig workflows"]) },
      { id: "02", label: "TOKENIZATION", body: "Real-world-asset tokenization engineering.", bullets: bullets(["RWA infrastructure"]) },
      { id: "03", label: "COMPLIANCE", body: "Travel Rule, KYT, and regulatory reporting for digital assets.", bullets: bullets(["KYT integrations"]) },
    ]
  ),

  "education": build(
    "// IND.EDU",
    "Education — student outcomes as the KPI.",
    "SIS, LMS, and analytics — engineered for institutions.",
    "We modernize institutional platforms — from SIS and LMS integration to student outcome analytics — with student privacy as a constraint.",
    [
      { id: "01", label: "PLATFORM.MODERNIZATION", body: "SIS/LMS modernization and integration.", bullets: bullets(["Ed-tech stack redesign"]) },
      { id: "02", label: "OUTCOMES.ANALYTICS", body: "Data platforms tied to real student outcomes.", bullets: bullets(["Outcome dashboards"]) },
      { id: "03", label: "STUDENT.PRIVACY", body: "FERPA-native architectures.", bullets: bullets(["FERPA compliance"]) },
    ]
  ),

  "government": build(
    "// IND.GOV",
    "Government — modernize service delivery.",
    "FedRAMP, StateRAMP, and citizen-first design.",
    "We help government agencies modernize citizen services — with FedRAMP/StateRAMP compliant platforms and accessibility-first design.",
    [
      { id: "01", label: "CITIZEN.SERVICES", body: "Accessible, mobile-first citizen service platforms.", bullets: bullets(["Section 508 native"]) },
      { id: "02", label: "SECURE.CLOUD", body: "FedRAMP and StateRAMP-aligned cloud architectures.", bullets: bullets(["FedRAMP moderate", "StateRAMP"]) },
      { id: "03", label: "MODERNIZATION", body: "Legacy system modernization with progressive migration.", bullets: bullets(["Legacy sunset plans"]) },
    ]
  ),

  "healthcare": build(
    "// IND.HEALTH",
    "Healthcare — HIPAA-native platform engineering.",
    "Payer, provider, and health-tech.",
    "We engineer healthcare platforms with HIPAA compliance and interoperability standards (FHIR, HL7) as core architecture constraints.",
    [
      { id: "01", label: "INTEROPERABILITY", body: "FHIR-native APIs and health data interoperability.", bullets: bullets(["FHIR R4", "HL7 v2 bridges"]) },
      { id: "02", label: "CLINICAL.PLATFORMS", body: "Clinical decision support and workflow platforms.", bullets: bullets(["CDS integrations"]) },
      { id: "03", label: "PAYER.OPERATIONS", body: "Claims processing modernization and utilization management.", bullets: bullets(["Claims automation"]) },
    ]
  ),

  "hospitality-leisure-travel": build(
    "// IND.HLT",
    "Hospitality, Leisure & Travel — guest experience, engineered.",
    "PMS, loyalty, and booking platforms.",
    "We modernize hospitality operators — from PMS integrations to loyalty programs and digital booking platforms.",
    [
      { id: "01", label: "PMS.MODERNIZATION", body: "Property management system modernization and integration.", bullets: bullets(["PMS-native integrations"]) },
      { id: "02", label: "LOYALTY", body: "Loyalty platform builds tied to real customer telemetry.", bullets: bullets(["Loyalty engines"]) },
      { id: "03", label: "BOOKING", body: "Direct-booking channel engineering and conversion optimization.", bullets: bullets(["Direct-booking uplift"]) },
    ]
  ),

  "insurance": build(
    "// IND.INS",
    "Insurance — policy admin, claims, and underwriting AI.",
    "P&C, life, and specialty lines.",
    "We modernize insurance core platforms — policy admin, claims automation, and AI-assisted underwriting.",
    [
      { id: "01", label: "POLICY.ADMIN", body: "Modern policy administration platform engineering.", bullets: bullets(["PAS modernization"]) },
      { id: "02", label: "CLAIMS.AI", body: "AI-assisted claims triage and fraud detection.", bullets: bullets(["Claims triage ML"]) },
      { id: "03", label: "UNDERWRITING", body: "Data-driven underwriting platforms.", bullets: bullets(["UW workbenches"]) },
    ]
  ),

  "manufacturing-distribution": build(
    "// IND.MFG",
    "Manufacturing & Distribution — smart factory to smart supply chain.",
    "MES, OT, and supply chain platforms.",
    "We integrate manufacturing execution, operational technology, and supply chain platforms — with cybersecurity as a non-negotiable.",
    [
      { id: "01", label: "SMART.FACTORY", body: "MES modernization and OT/IT integration.", bullets: bullets(["OT/IT convergence"]) },
      { id: "02", label: "OT.CYBER", body: "Operational technology cybersecurity hardening.", bullets: bullets(["Purdue model", "Zero-trust OT"]) },
      { id: "03", label: "SUPPLY.CHAIN", body: "Supply chain visibility and demand planning.", bullets: bullets(["Multi-tier visibility"]) },
    ]
  ),

  "mortgage-consumer-lending": build(
    "// IND.MORTGAGE",
    "Mortgage & Consumer Lending — origination to servicing.",
    "Digital origination and portfolio operations.",
    "We modernize lenders — from digital origination through servicing platforms — with regulatory compliance baked in.",
    [
      { id: "01", label: "ORIGINATION", body: "Digital origination platforms with automated underwriting.", bullets: bullets(["Digital POS"]) },
      { id: "02", label: "SERVICING", body: "Servicing platforms with default management and loss mitigation.", bullets: bullets(["Loss mit workflows"]) },
      { id: "03", label: "COMPLIANCE", body: "TRID, HMDA, and CFPB compliance engineering.", bullets: bullets(["Regulatory reporting"]) },
    ]
  ),

  "non-profit": build(
    "// IND.NONPROFIT",
    "Non-Profit — mission delivery, engineered.",
    "Donor platforms, program ops, and impact reporting.",
    "For non-profit and mission-driven organizations, we build donor platforms, operational tooling, and impact reporting systems.",
    [
      { id: "01", label: "DONOR.PLATFORMS", body: "Modern donor and CRM platforms.", bullets: bullets(["Salesforce NPSP"]) },
      { id: "02", label: "PROGRAM.OPS", body: "Program delivery platforms tied to outcomes.", bullets: bullets(["Outcome tracking"]) },
      { id: "03", label: "IMPACT.REPORTING", body: "Impact measurement and public reporting.", bullets: bullets(["Public dashboards"]) },
    ]
  ),

  "oil-gas-resources": build(
    "// IND.OGR",
    "Oil, Gas & Resources — digital operations and energy transition.",
    "Upstream, midstream, and downstream digital.",
    "We help energy and resource operators digitize operations while preparing for the energy transition.",
    [
      { id: "01", label: "DIGITAL.OPS", body: "Field-force digital enablement and production optimization.", bullets: bullets(["Production twin"]) },
      { id: "02", label: "SAFETY", body: "Safety telemetry and incident response platforms.", bullets: bullets(["Safety dashboards"]) },
      { id: "03", label: "TRANSITION", body: "Energy transition strategy and platform engineering.", bullets: bullets(["Renewables platforms"]) },
    ]
  ),

  "payments": build(
    "// IND.PAY",
    "Payments — infrastructure at scale.",
    "Issuing, acquiring, and cross-border rails.",
    "We build payments platforms — from issuer processing to acquiring infrastructure and cross-border rails.",
    [
      { id: "01", label: "ISSUING", body: "Card issuing platform engineering.", bullets: bullets(["Issuer processing"]) },
      { id: "02", label: "ACQUIRING", body: "Merchant acquiring and gateway platforms.", bullets: bullets(["Gateway engineering"]) },
      { id: "03", label: "CROSS.BORDER", body: "Cross-border payment rails and FX.", bullets: bullets(["Cross-border FX"]) },
    ]
  ),

  "pharma-life-sciences": build(
    "// IND.PHARMA",
    "Pharma & Life Sciences — clinical to commercial.",
    "Clinical data, R&D, and commercial ops.",
    "We build platforms for clinical operations, R&D data, and commercial teams — with GxP compliance engineered in.",
    [
      { id: "01", label: "CLINICAL.DATA", body: "Clinical data platforms with 21 CFR Part 11 compliance.", bullets: bullets(["Part 11 native"]) },
      { id: "02", label: "R&D", body: "R&D data platforms and computational biology infrastructure.", bullets: bullets(["Comp bio pipelines"]) },
      { id: "03", label: "COMMERCIAL", body: "Field-force enablement and HCP engagement platforms.", bullets: bullets(["HCP platforms"]) },
    ]
  ),

  "power-utilities-renewables": build(
    "// IND.PUR",
    "Power, Utilities & Renewables — grid-scale digital.",
    "Grid ops, DER, and renewables portfolio.",
    "We modernize utility operators — grid operations, distributed energy resources, and renewables portfolio platforms.",
    [
      { id: "01", label: "GRID.OPS", body: "Grid monitoring and outage management.", bullets: bullets(["Outage mgmt", "SCADA integrations"]) },
      { id: "02", label: "DER", body: "Distributed energy resource management platforms.", bullets: bullets(["DERMS builds"]) },
      { id: "03", label: "RENEWABLES", body: "Renewables portfolio management and forecasting.", bullets: bullets(["Portfolio dashboards"]) },
    ]
  ),

  "private-equity": build(
    "// IND.PE",
    "Private Equity — value-creation engineering.",
    "Portfolio operations and diligence.",
    "For PE firms and their portfolio companies, we deliver tech-enabled diligence, 100-day plans, and value-creation platform builds.",
    [
      { id: "01", label: "DILIGENCE", body: "Tech and cyber diligence with data-driven findings.", bullets: bullets(["Tech DD", "Cyber DD"]) },
      { id: "02", label: "100.DAY", body: "First-100-days operating platform stand-up.", bullets: bullets(["100-day plans"]) },
      { id: "03", label: "VALUE.CREATION", body: "Long-arc value creation engineering for portcos.", bullets: bullets(["VC playbooks"]) },
    ]
  ),

  "retail": build(
    "// IND.RETAIL",
    "Retail — unified commerce and store operations.",
    "Omnichannel, store ops, and merchandising.",
    "We modernize retail operators — unified commerce platforms, store operations, and merchandising systems.",
    [
      { id: "01", label: "UNIFIED.COMMERCE", body: "Composable commerce and OMS platforms.", bullets: bullets(["OMS engineering"]) },
      { id: "02", label: "STORE.OPS", body: "Store operations, workforce, and clienteling.", bullets: bullets(["Clienteling apps"]) },
      { id: "03", label: "MERCHANDISING", body: "Assortment planning and pricing optimization.", bullets: bullets(["Pricing AI"]) },
    ]
  ),

  "technology-media-telecom": build(
    "// IND.TMT",
    "Technology, Media & Telecom — for the firms that build technology.",
    "Product engineering for TMT.",
    "For technology, media, and telecom operators, we build the products they ship — from platform engineering to content delivery to network operations.",
    [
      { id: "01", label: "PRODUCT.ENG", body: "Platform product engineering at scale.", bullets: bullets(["Multi-tenant platforms"]) },
      { id: "02", label: "CONTENT.DELIVERY", body: "Content platforms, DAM, and streaming infrastructure.", bullets: bullets(["Streaming stacks"]) },
      { id: "03", label: "NETWORK.OPS", body: "Network operations telemetry and automation.", bullets: bullets(["Network AIOps"]) },
    ]
  ),

  "transportation-logistics": build(
    "// IND.TLOG",
    "Transportation & Logistics — real-time visibility.",
    "TMS, WMS, and fleet intelligence.",
    "We modernize carriers, 3PLs, and shippers with TMS platforms, WMS integration, and fleet intelligence.",
    [
      { id: "01", label: "TMS", body: "Transportation management platforms with rate optimization.", bullets: bullets(["TMS engineering"]) },
      { id: "02", label: "WMS", body: "Warehouse management systems and automation integration.", bullets: bullets(["WMS integrations"]) },
      { id: "03", label: "FLEET.AI", body: "Fleet telemetry and predictive maintenance.", bullets: bullets(["Predictive maintenance"]) },
    ]
  ),
};
